import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { BoxType } from "notifications/values/box-type/box-type";
import { z } from "zod";

export type NotificationFilterJSON = z.infer<typeof NotificationFilterSchema.NotificationFilterJSONZ>;

export type NotificationfilterParametersProps = z.infer<typeof NotificationFilterSchema.NotificationFilterZ>;

export default class NotificationFilterSchema {
  public static readonly NotificationFilterJSONZ = z.object({
    query: z.string().nullish(),
    boxType: z.enum(BoxType).nullish(),
    type: z.string().nullish(),
    from: z.string().nullish(),
    to: z.string().nullish(),
    description: z.string().nullish(),
    minCreatedDate: z.iso.datetime({ offset: true }).nullish(),
    maxCreatedDate: z.iso.datetime({ offset: true }).nullish(),
    isUnread: z.boolean().nullish(),
    isActionRequired: z.boolean().nullish(),
    isMessageNotification: z.boolean().nullish(),
    topicId: z.uuid().nullish(),
  });
  public static readonly NotificationFilterZ = z.object({
    query: z.string().optional(),
    boxType: z.enum(BoxType).optional(),
    type: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    description: z.string().optional(),
    minCreatedDate: DateSchema.type.optional(),
    maxCreatedDate: DateSchema.type.optional(),
    isUnread: z.boolean().optional(),
    isActionRequired: z.boolean().optional(),
    isMessageNotification: z.boolean().optional(),
    topicId: z.instanceof(Guid).optional(),
  });

  public static validateJSON(values: unknown): NotificationfilterParametersProps {
    return parseOrThrow(NotificationFilterSchema.NotificationFilterZ, values, {
      logError: true,
      schemaName: "NotificationFilterSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValidJSON(values: unknown): values is NotificationfilterParametersProps {
    try {
      NotificationFilterSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: NotificationFilterJSON): NotificationfilterParametersProps {
    const parsed = parseOrThrow(NotificationFilterSchema.NotificationFilterJSONZ, data, {
      logError: true,
      schemaName: "NotificationFilterSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return NotificationFilterSchema.codec.decode(parsed);
  }

  public static serialize(filter: NotificationfilterParametersProps): NotificationFilterJSON {
    const parsed = NotificationFilterSchema.validateJSON(filter);
    return NotificationFilterSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    NotificationFilterSchema.NotificationFilterJSONZ,
    NotificationFilterSchema.NotificationFilterZ,
    {
      decode: (data) => ({
        query: data.query ?? undefined,
        boxType: data.boxType ?? undefined,
        type: data.type ?? undefined,
        from: data.from ?? undefined,
        to: data.to ?? undefined,
        description: data.description ?? undefined,
        minCreatedDate: AHDate.fromJSON(data.minCreatedDate) ?? undefined,
        maxCreatedDate: AHDate.fromJSON(data.maxCreatedDate) ?? undefined,
        isUnread: data.isUnread ?? undefined,
        isActionRequired: data.isActionRequired ?? undefined,
        isMessageNotification: data.isMessageNotification ?? undefined,
        topicId: Guid.fromJSON(data.topicId) ?? undefined,
      }),
      encode: (filter) => ({
        query: filter.query,
        boxType: filter.boxType,
        type: filter.type,
        from: filter.from,
        to: filter.to,
        description: filter.description,
        minCreatedDate: filter.minCreatedDate?.toISOString(),
        maxCreatedDate: filter.maxCreatedDate?.toISOString(),
        isUnread: filter.isUnread,
        isActionRequired: filter.isActionRequired,
        isMessageNotification: filter.isMessageNotification,
        topicId: filter.topicId?.toJSON(),
      }),
    }
  );
}
